import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, Link, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import CodeIcon from '@mui/icons-material/Code';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Controller, useForm } from 'react-hook-form';
import { mobilizeiUsersService } from '../../../services/mobilizei/users.service';
import useAuthentication from '../../../hooks/authentication.hook';
import { showApiError } from '../../../utils/errors';
import ecommercePlatforms from '../../../utils/ecommercePlatforms';
import { useOnboarding } from '../../../contexts/onboarding.context';
import { MbTextField } from '../../../components/MbTextFields';
import { COLORS } from '../../Onboarding/_shared/tokens';
import MainLayout from '../../../layouts/main';
import HelloContainer from '../HelloContainer';

const BENEFITS = [
    { Icon: BoltIcon, label: 'app em minutos' },
    { Icon: CodeIcon, label: 'sem código' },
    { Icon: CreditCardOffIcon, label: 'sem compromisso' }
];

export default function Signup() {
    const { control, handleSubmit, register, setValue, formState: { errors } } = useForm();

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { setToken } = useAuthentication();
    const { data, setData } = useOnboarding();

    // phoneNumber has no visible field, but it may arrive from a platform
    // deep-link (client_telephone). Register it so setValue below is submitted.
    useEffect(() => {
        register('phoneNumber');
    }, [register]);

    const onSubmit = (data) => {
        setIsLoading(true);

        if (data.phoneNumber) {
            data.phoneNumber = '+55' + data.phoneNumber;
        }

        if (data.email) {
            data.email = data.email.trim()
        }

        mobilizeiUsersService.create(data)
            .then(response => {
                setToken(response.token);
            })
            .catch(e => {
                showApiError(e.error);
                setIsLoading(false)
            })
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ecommercePlatformId = urlParams.get('platform_id');
        const storeId = urlParams.get('store_id');
        const storeName = urlParams.get('store_name');
        const storeUrl = urlParams.get('store_url');
        const platformToken = urlParams.get("platform_token");
        const clientName = urlParams.get("client_name");
        const clientTelephone = urlParams.get("client_telephone");
        const clientEmail = urlParams.get("client_email");

        // Set form values
        if (clientName) setValue('name', clientName)
        if (clientTelephone) setValue('phoneNumber', clientTelephone);
        if (clientEmail) setValue('email', clientEmail);

        if (ecommercePlatformId && storeId && storeName && storeUrl) {
            const selectedPlatform = ecommercePlatforms.getById(ecommercePlatformId);
            if (selectedPlatform) {
                setData({
                    ...data,
                    ecommercePlatformId: selectedPlatform.id,
                    ecommercePlatformName: selectedPlatform.name,
                    ecommercePlatformToken: platformToken,
                    ecommercePlatformAllowsIframe: selectedPlatform.isIframeAllowed,
                    storeAccountUrl: storeUrl + selectedPlatform.storeAccountUrl,
                    storeCartUrl: storeUrl + selectedPlatform.storeCartUrl,
                    storeId,
                    storeName,
                    storeUrl
                })
            }
        }
    }, [])

    // Personalized copy when the user lands from a platform deep-link.
    const hasStoreContext = Boolean(data.storeName || data.ecommercePlatformName);

    return (
        <MainLayout>
            <Grid container display="flex" direction="row" spacing={2} paddingX={{ xs: 2, lg: 20 }}>
                <Grid item xs={12} lg={6}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        marginTop: 6,
                        paddingBottom: 10,
                        minHeight: '100vh',
                    }}
                >
                    {/* Form */}
                    <Box sx={{ maxWidth: 440, width: '100%', mx: { xs: 'auto', md: 0 } }}>
                        <Typography
                            component="h1"
                            sx={{ fontWeight: 700, fontSize: { xs: 36, md: 46 }, lineHeight: 1.1, letterSpacing: '-0.5px', mb: 1.5 }}
                        >
                            Crie sua conta e veja seu app <Box component="span" sx={{ color: COLORS.green }}>hoje.</Box>
                        </Typography>

                        <Typography sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.5, color: COLORS.textMuted, mb: 4 }}>
                            {hasStoreContext ? (
                                <>
                                    Já ajudamos centenas de lojas {data.ecommercePlatformName || ''} a impulsionar suas ações de marketing e vendas. Agora, você também pode ter o aplicativo da {data.storeName ? <b>{data.storeName}</b> : 'sua loja'} pronto para ser publicado em poucos minutos.
                                </>
                            ) : (
                                'Em poucos minutos você testa tudo no seu celular e recebe a primeira notificação da sua loja.'
                            )}
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                            <Stack spacing={2}>
                                <Controller
                                    name="name"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: "O nome completo é obrigatório." }}
                                    render={({ field }) => (
                                        <MbTextField
                                            label="Nome"
                                            placeholder="Como podemos te chamar?"
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.name?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="email"
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        required: "O e-mail é obrigatório.",
                                        pattern: {
                                            value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}|s$/,
                                            message: "O e-mail não é válido."
                                        }
                                    }}
                                    render={({ field }) => (
                                        <MbTextField
                                            label="E-mail"
                                            type="email"
                                            placeholder="voce@sualoja.com.br"
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.email?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="password"
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        required: "A senha é obrigatória.",
                                        minLength: { value: 8, message: "A senha deve ter no mínimo 8 caracteres." }
                                    }}
                                    render={({ field }) => (
                                        <MbTextField
                                            label="Senha"
                                            type={showPassword ? 'text' : 'password'}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.password?.message}
                                            helper="Mínimo de 8 caracteres."
                                            suffix={
                                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)} aria-label="mostrar senha">
                                                    {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                                </IconButton>
                                            }
                                        />
                                    )}
                                />
                            </Stack>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                                endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                                sx={{ mt: 3, fontSize: 16, py: 2, boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                            >
                                testar 7 dias grátis
                            </Button>
                        </Box>

                        {/* Benefit chips — mobile only; desktop shows the persuasion column instead */}
                        <Stack direction="row" flexWrap="wrap" gap={1.25} justifyContent="center" sx={{ mt: 2.25, display: { xs: 'flex', md: 'none' } }}>
                            {BENEFITS.map(({ Icon, label }) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.875,
                                        backgroundColor: COLORS.surfaceMuted,
                                        borderRadius: '999px'
                                    }}
                                >
                                    <Icon sx={{ fontSize: 15, color: COLORS.green }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{label}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Typography sx={{ mt: 2.5, fontSize: 13, lineHeight: 1.5, color: COLORS.textMuted, textAlign: 'center' }}>
                            Ao criar a conta, você concorda com os nossos{' '}
                            <Link href="https://mobilizei.com.br/termos-de-uso" target="_blank">Termos de Uso</Link> e nossa{' '}
                            <Link href="https://mobilizei.com.br/politica-de-privacidade" target="_blank">Política de Privacidade</Link>.
                        </Typography>

                        <Typography sx={{ mt: 3, fontSize: 16, color: COLORS.textMuted, textAlign: 'center' }}>
                            Já tem uma conta?{' '}
                            <Link href="/login" sx={{ fontWeight: 700 }}>Faça seu login.</Link>
                        </Typography>
                    </Box>
                </Grid>

                <HelloContainer benefits={BENEFITS} />
            </Grid>
        </MainLayout >
    );
}